import cls from "classnames";
import { useTranslation } from "react-i18next";

import logoSpinner from "images/logoSpinner.svg";

import styles from "./index.module.scss";

type SpinnerProps = {
  /**
   * Where it sits:
   * - `block` (default) — centred across the line, with room above and below
   * - `inline` — sharing a line with a label or an icon slot
   * - `fill` — centred in the space a flex or grid parent gives it, both axes
   */
  placement?: "block" | "inline" | "fill";
  size?: "small" | "medium" | "large";
  /** Overrides the brand colour, for a ring that has to read against a fill. */
  color?: string;
  /** Overrides the generic name, for a wait the reader can be told more about. */
  label?: string;
  /** Free to set anything the kit does: its own rules are layered under yours. */
  className?: string;
};

/**
 * A wait standing in for content that has not arrived. It places itself — centred,
 * with room above and below — so the common case is `<Spinner />` and no wrapper.
 *
 * `placement` covers the two surroundings that need something else.
 */
export default function Spinner({
  placement = "block",
  size = "medium",
  color,
  label,
  className,
}: SpinnerProps) {
  const { t } = useTranslation();
  const boxed = placement === "fill";

  const ring = (
    <span
      role="progressbar"
      aria-label={label ?? t("Common.spinner.loadingLabel")}
      className={cls(
        styles.spinner,
        styles[size],
        !boxed && styles[placement],
        !boxed && className
      )}
      // Set only when asked for: a default here would outrank the stylesheets too.
      style={color ? { color } : undefined}
    />
  );

  // Claiming a space and centring in it takes a box: auto margins only reach the
  // second of those, and only where the parent is already flex or grid.
  return boxed ? <span className={cls(styles.fill, className)}>{ring}</span> : ring;
}

// An <img>'s width attribute loses to any stylesheet, so this stays overridable.
const LOGO_WIDTH = { medium: 40, large: 64 };

type LogoSpinnerProps = {
  size?: keyof typeof LOGO_WIDTH;
  label?: string;
  className?: string;
};

/**
 * The branded wait, for a whole screen or a whole dialog — the two places a reader
 * has nothing else to look at. Anything smaller than that is a `Spinner`.
 *
 * It claims the surface it is given and centres in it, which is always the whole of one.
 */
export function LogoSpinner({ size = "medium", label, className }: LogoSpinnerProps) {
  const { t } = useTranslation();

  return (
    <span className={cls(styles.fill, className)}>
      <img
        src={logoSpinner}
        alt={label ?? t("Common.spinner.loadingLabel")}
        width={LOGO_WIDTH[size]}
        className={styles.logo}
      />
    </span>
  );
}
